import React, { ChangeEvent, useEffect, useState } from 'react';
import WhiteShieldIcon from '../../../../../components/resources/images/white-shield.svg';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { openDialog } from '../../../../../app/dialog/dialog.actions';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { useDispatch } from 'react-redux';
import {
  updateUserProfile,
  updateUserEmail,
  deleteUser,
} from '../../../../../app/page/page-storage/users/users.actions';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { getValidTimezones } from '../../../../../utils/date.utils';
import { validateEmail } from '../../../../../utils/user.utils';
import { useApiRequestState } from '../../../../../utils/api.utils';
import Loader from 'react-loader-spinner';
import { CircularAvatar } from '../../../common/CustomAvatar';
import { addFlashMessage } from '../../../../../utils/flashMessages.utils';

const timezoneNames = getValidTimezones();
const timezoneOptions = timezoneNames.map((tz) => {
  return { value: tz.value, label: tz.label };
});

const localeOptions = [
  { value: 'en_US', label: 'United States' },
  { value: 'en_GB', label: 'Europe' },
];

export const BasicInfo = () => {
  const dispatch = useDispatch();
  const loggedUserId = useSelect(authSelectors.selectLoggedUser) || '';
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId);
  const [isUpdatingProfile] = useApiRequestState('updateUser');
  const [isUpdatingUserEmail] = useApiRequestState('updateUserEmail');

  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);
  const [showDateNumberDropdown, setShowDateNumberDropdown] = useState(false);

  const [selectedLocale, setDateAndNumberFormat] = useState('en_US');
  const [email, setEmail] = useState(loggedUser?.email);
  const [selectedTimezoneOption, setSelectedTimezoneOption] = useState<{ value: string; label: string }>();
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(
    loggedUser?.firstDayOfWeek ? parseInt(loggedUser?.firstDayOfWeek) : 1,
  ); // 0 Sunday 1 Monday ...
  const [personalInfoData, setPersonalInfoData] = useState({
    firstName: loggedUser?.firstName,
    lastName: loggedUser?.lastName,
    phone: loggedUser?.phone,
  });

  useEffect(() => {
    const userTimezoneOption = timezoneOptions.find((tz) => tz.value === loggedUser?.timezone);
    setSelectedTimezoneOption(userTimezoneOption);
  }, [loggedUser]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPersonalInfoData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleUpdateUserProfile = () => {
    const updatedData = {
      firstName: personalInfoData.firstName,
      lastName: personalInfoData.lastName,
      phone: personalInfoData.phone,
      timezone: selectedTimezoneOption?.value,
      locale: selectedLocale,
      firstDayOfWeek: firstDayOfWeek,
    };
    dispatch(updateUserProfile(updatedData));
  };

  const handleUpdateUserEmailClick = () => {
    if (email) {
      const updatedData = {
        email: email,
      };
      dispatch(updateUserEmail(updatedData));
    }
  };

  const isEmailValid = validateEmail(email);

  const getBase64 = (file: File) => {
    return new Promise((resolve) => {
      let baseURL;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        baseURL = reader.result;
        resolve(baseURL);
      };
    });
  };

  const handleProfileImageUpdate = (e: ChangeEvent<HTMLInputElement>) => {
    const MAX_SIZE_FOR_IMG = 512000; // 500 KB
    if (e.target.files && e.target.files[0]) {
      if (e.target.files[0].size <= MAX_SIZE_FOR_IMG) {
        getBase64(e.target.files[0]).then((result) => dispatch(updateUserProfile({ image: String(result) })));
      } else {
        addFlashMessage({
          type: 'error',
          message: 'The selected image exceeds the allowed size limit. 500 KB is the maximum size.',
        });
      }
    }
  };

  const handleTimezoneChange = (selectedTz: { value: string; label: string }) => {
    setSelectedTimezoneOption(selectedTz);
  };

  // @TODO: these following functions were put here in order to capture the
  // onChange of the input, as required by React, otherwise there was a warning
  // We need to improve this code to make it cleaner
  const handleFirstDayOfTheWeekChange0 = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.value === 'on';
    if (isChecked) {
      setFirstDayOfWeek(0);
    } else {
      setFirstDayOfWeek(1);
    }
  };
  const handleFirstDayOfTheWeekChange1 = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.value === 'on';
    if (isChecked) {
      setFirstDayOfWeek(1);
    } else {
      setFirstDayOfWeek(0);
    }
  };

  return (
    <div className="col px-0">
      <div className="field-container px-2 mb-5">
        <div className="panel green-gradient-2 py-1 px-2 flex aic mb-3">
          <h2 className="f-14 white">ACCOUNT INFO</h2>
        </div>
        <div className="row">
          <div className="col-12 pr-1 flex aic">
            <span className="bigger-avatar flex aic jcc mr-1 gray-bg">
              <CircularAvatar
                src={loggedUser?.image}
                title={`${loggedUser?.firstName} ${loggedUser?.lastName}`}
                cursorPointer={true}
                size="100"
              />
              <input
                type="file"
                name="profile-image"
                className="hidden-input-file"
                onChange={handleProfileImageUpdate}
                style={{ cursor: 'pointer' }}
              />
            </span>
            <div className="flex aic ml-2">
              <div className="flex-vertical">
                <div className="col-12">
                  <input
                    type="text"
                    name="firstName"
                    className="small-input no-border f-24"
                    placeholder="First Name"
                    value={personalInfoData.firstName}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    name="lastName"
                    className="small-input no-border f-24"
                    placeholder="Last Name"
                    value={personalInfoData?.lastName}
                    onChange={handleChange}
                  />
                  {loggedUser?.isAdmin === '1' && (
                    <a href="/" className="btn btn-md blue-bg flex-inline mt-1 no-hover" title="Admin">
                      <img src={WhiteShieldIcon} alt="" className="img-fluid mr-05" /> Admin
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-12 pr-1">
            <div className="field-container mb-0">
              <label htmlFor="account-new-project-name" className="f-14">
                Email Address
              </label>
              <p>
                <small className="f-1 blueGray">
                  Change your email address. You’ll need to check your email and verify this new address.
                </small>
              </p>
              <div className="flex mt-1">
                <input
                  type="text"
                  className="medium-input max-330"
                  name="email"
                  id="account-new-url"
                  placeholder="Email"
                  value={email || ''}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button
                  className={`btn ${loggedUser?.email === email ? 'darkGray-bg' : ''}`}
                  title="Save"
                  onClick={handleUpdateUserEmailClick}
                  disabled={loggedUser?.email === email}
                >
                  {isUpdatingUserEmail ? (
                    <span className="css-loader" style={{ height: 20, width: 20 }}></span>
                  ) : (
                    'Save'
                  )}
                </button>
                <span className={`pt-1 ml-1 ${isEmailValid ? 'green' : 'red'}`}>
                  <img src="images/green-shield.svg" alt="" className="img-fluid" />
                  {isEmailValid ? 'Valid' : 'Invalid Email'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-6 pr-1">
            <form className="field-container mb-0">
              <label htmlFor="account-new-project-name" className="f-14">
                Phone Number
              </label>
              <input
                type="text"
                name="phone"
                className="medium-input max-330"
                onChange={handleChange}
                value={personalInfoData.phone || ''}
              />
            </form>
          </div>
          <div className="col-12 mt-1">
            <p>
              <small className="blue f-10">
                We may use this phone number to contact you about security events, sending workflow SMS, and for owner
                property values.
              </small>
            </p>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-6 pr-1">
            <div className="field-container mb-0">
              <label htmlFor="account-new-project-name" className="f-14">
                Time Zone
              </label>
              <div
                className="custom-select small-select"
                onClick={() => setShowTimezoneDropdown(!showTimezoneDropdown)}
              >
                <input type="hidden" name="" id="state" />
                <span className={`select-field ${showTimezoneDropdown ? 'active' : ''}`}>
                  <span className="select-value">{selectedTimezoneOption?.label ?? 'Select a time zone'}</span>{' '}
                  <i className="icon-down-dir" />
                </span>
                <div
                  className="select-dropdown"
                  style={showTimezoneDropdown ? { display: 'block' } : { display: 'none' }}
                >
                  {timezoneOptions.map((tz, idx) => (
                    <div
                      key={idx}
                      className="select-item"
                      data-change={tz.label}
                      data-val={tz.value}
                      onClick={() => handleTimezoneChange(tz)}
                    >
                      {tz.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr />
        {/*<div className="row">
          <div className="col-6 pr-1">
            <div className="field-container mb-0">
              <label htmlFor="account-new-project-name" className="f-14">
                Date & Number Format
              </label>
              <div
                className="custom-select small-select"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDateNumberDropdown(!showDateNumberDropdown);
                }}
              >
                <input type="hidden" name="" id="state" />
                <span className={`select-field ${showDateNumberDropdown ? 'active' : ''}`}>
                  <span className="select-value">
                    {localeOptions.find((localeOption) => localeOption.value === selectedLocale)?.label}
                  </span>{' '}
                  <i className="icon-down-dir"></i>
                </span>
                <div
                  className="select-dropdown"
                  style={showDateNumberDropdown ? { display: 'block' } : { display: 'none' }}
                >
                  {localeOptions.map((localeOption) => (
                    <div
                      key={localeOption.value}
                      className="select-item"
                      data-change={localeOption.value}
                      data-val={localeOption.value}
                      onClick={() => setDateAndNumberFormat(localeOption.value)}
                    >
                      {localeOption.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
           <div className="col-12 mt-1">
            <p>
              <small className="f-10 blue">Format: 07/15/2020 and 1,234.56.</small>
            </p>
          </div> 
        </div>*/}
        {/* <hr />
                <div className="row">
          <div className="col-6 pr-1">
            <div className="field-container mb-1">
              <label htmlFor="account-new-project-name" className="f-14">
                First Day of The Week
              </label>
              <div className="remember-checkbox flex aic jcl mt-2">
                <input
                  type="checkbox"
                  name="firstDayOfTheWeek"
                  id="projects-report-checkbox-sunday"
                  checked={firstDayOfWeek === 0}
                  onChange={handleFirstDayOfTheWeekChange0}
                />
                <label htmlFor="projects-report-checkbox-sunday">Sunday</label>
              </div>
            </div>
            <div className="field-container mb-0">
              <div className="remember-checkbox flex aic jcl">
                <input
                  type="checkbox"
                  name="firstDayOfTheWeek"
                  id="projects-report-checkbox-monday"
                  checked={firstDayOfWeek === 1}
                  onChange={handleFirstDayOfTheWeekChange1}
                />
                <label htmlFor="projects-report-checkbox-monday">Monday</label>
              </div>
            </div>
          </div>
          <div className="col-12 mt-1">
            <p>
              <small className="f-10 blue">
                Change the first day of the week in the date picker when you update a date column in a Board.
              </small>
            </p>
          </div>
        </div>
        <hr /> */}
        <div className="row">
          <div className="col-12 flex">
            <button className="btn" title="Save" onClick={handleUpdateUserProfile}>
              {isUpdatingProfile ? <Loader type="TailSpin" color="#1bc3a7" height={20} width={20} /> : 'Save'}
            </button>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-6 flex aic jcr">
            <p
              className="link red popup_button f-1"
              data-popup="delete_popup"
              onClick={() =>
                dispatch(
                  openDialog('deleteDialog', {
                    onConfirm: () => dispatch(deleteUser(loggedUserId)),
                    title: 'Confirmation',
                    message:
                      'Are you sure you want to leave this workspace? You will lose access until a workspace admin restores you.',
                  }),
                )
              }
            >
              Leave this workspace
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
